import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Pagination from "../../Helpers/Pagination";
import { Modal, ModalBody, Row, Col, Card, CardBody, Button, Table, Badge } from "reactstrap";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { LocalizationProvider, TimePicker, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Select from "react-select";
import {
    Clock, Phone, CreditCard, ShieldCheck,
    Calendar, User, Zap, DollarSign, ArrowRight, Briefcase, Factory
} from "lucide-react";
import { toast } from "react-toastify";
import DeleteModal from "../Common/DeleteModal";

const DriverList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [data, setData] = useState([]);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [viewItem, setViewItem] = useState(null);
    const [attendanceModal, setAttendanceModal] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);

    const [salaryData, setSalaryData] = useState(null);
    const [salaryFilter, setSalaryFilter] = useState({
        from_date: dayjs().startOf('month'),
        to_date: dayjs()
    });

    const [attendance, setAttendance] = useState({
        attendance_date: dayjs(),
        status: { value: "present", label: "Present" },
        check_in: null,
        check_out: null
    });

    const limit = 20;
    const statusOptions = [
        { value: "present", label: "Present" },
        { value: "absent", label: "Absent" }
    ];

    const pickerStyleProps = {
        textField: {
            size: "small",
            fullWidth: true,
            variant: "outlined",
            sx: {
                '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: '#f8f9fa',
                    height: '45px'
                },
            }
        },
        popper: { sx: { zIndex: 99999 } }
    };

    const selectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '45px',
            borderRadius: '8px',
            backgroundColor: '#f8f9fa',
            borderColor: '#e2e8f0',
            boxShadow: 'none',
            '&:hover': { borderColor: '#cbd5e1' }
        }),
        menuPortal: base => ({ ...base, zIndex: 99999 })
    };

    /* ================= FETCH DATA ================= */
    const loadDrivers = async () => {
        dispatch(loadingTrue());
        const offset = (page - 1) * limit;
        const res = await Handler({
            method: "get",
            url: `/driver/list.php?limit=${limit}&offset=${offset}&search=${search}&status=active`
        });
        if (res?.success) { // Match PHP Response "status"
            setData((res?.data || []).filter(d => d.status === 'active'));
            setTotal(res.total_count);
        }
        dispatch(loadingFalse());
    };

    const fetchSalaryReport = async (driverId) => {
        if (!salaryFilter.from_date || !salaryFilter.to_date) return;
        dispatch(loadingTrue());
        const res = await Handler({
            method: "get",
            url: `/driver/salary_report.php?driver_id=${driverId}&from_date=${salaryFilter.from_date.format("YYYY-MM-DD")}&to_date=${salaryFilter.to_date.format("YYYY-MM-DD")}`
        });
        if (res?.success) {
            setSalaryData(res.data);
        } else {
            setSalaryData(null);
            toast.error(res.message || "Failed to calculate salary");
        }
        dispatch(loadingFalse());
    };

    const toggleStatus = async (id, status) => {
        dispatch(loadingTrue());
        const res = await Handler({
            method: "post",
            url: "/driver/update.php",
            data: { id, status: status === "active" ? "inactive" : "active" }
        });
        if (res.success) loadDrivers();
        dispatch(loadingFalse());
    };

    const saveAttendance = async () => {
        if (!selectedDriver) return;
        dispatch(loadingTrue());
        const payload = {
            driver_id: selectedDriver.id,
            attendance_date: attendance.attendance_date.format("YYYY-MM-DD"),
            status: attendance.status.value,
            check_in: attendance.check_in ? attendance.check_in.format("HH:mm:ss") : null,
            check_out: attendance.check_out ? attendance.check_out.format("HH:mm:ss") : null
        };
        const res = await Handler({ method: "post", url: "/attendance/create.php", data: payload });
        if (res?.success) {
            setAttendanceModal(false);
            setSelectedDriver(null);
            toast.success("Attendance Saved");
        }
        dispatch(loadingFalse());
    };

    useEffect(() => { loadDrivers() }, [page, search]);

    return (
        <div className="page-card">
            <div className="page-header">
                <h4>Drivers Management</h4>
                <div className="search-box">
                    <i className='bx bx-search'></i>
                    <input placeholder="Search drivers/company..." value={search}
                        onChange={e => { setPage(1); setSearch(e.target.value) }} />
                </div>
            </div>

            <div className="table-wrap">
                <table className="table modern-table">
                    <thead>
                        <tr>
                            <th>Driver Name</th>
                            <th>Company</th>
                            <th>Phone</th>
                            <th>License No</th>
                            <th>Status</th>
                            <th>Change Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.length === 0 ? (
                            <tr><td colSpan="7" className="text-center py-4">No drivers found</td></tr>
                        ) : data?.map(d => (
                            <tr key={d.id}>
                                <td>
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="avatar-circle bg-primary text-white shadow-sm">
                                            {d.name?.charAt(0)}
                                        </div>
                                        <span className="fw-bold">{d.name}</span>
                                    </div>
                                </td>
                                <td>{d.company_name}</td>
                                <td>{d.phone}</td>
                                <td className="text-uppercase text-muted small">{d.license_number}</td>
                                <td><span className={`status-badge ${d.status}`}>{d.status}</span></td>
                                <td>
                                    <label className="switch">
                                        <input type="checkbox" checked={d.status === "active"} onChange={() => toggleStatus(d.id, d.status)} />
                                        <span className="slider"></span>
                                    </label>
                                </td>
                                <td>
                                    <div className="d-inline-flex align-items-center gap-1 text-nowrap">
                                        <button
                                            className="icon-btn view"
                                            title="Salary Report"
                                            onClick={() => navigate(`/drivers/jobs/${d.id}`)}
                                        >
                                            <DollarSign size={16} />
                                        </button>
                                        <button className="icon-btn edit" onClick={() => navigate(`/drivers/edit/${d.id}`)}><i className='bx bx-edit'></i></button>
                                         <button className="icon-btn text-danger" title="Delete Driver"
                                             onClick={() => setDeleteTarget(d)}>
                                             <i className='bx bx-trash'></i>
                                         </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Pagination page={page} total={total} limit={limit} onPrev={() => setPage(p => Math.max(1, p - 1))} onNext={() => setPage(p => p + 1)} />

            {/* ✅ VIEW MODAL - REPLACED ESTIMATED WITH PHP API SALARY RATES */}
            <Modal isOpen={!!viewItem} toggle={() => setViewItem(null)} centered size="xl" className="extraordinary-modal">
                <ModalBody className="p-0 overflow-hidden">
                    <div className="modal-banner premium-gradient">
                        <div className="banner-content p-4 text-white">
                            <div className="d-flex align-items-center gap-3">
                                <div className="avatar-large avatar-circle border-white">{viewItem?.name?.charAt(0)}</div>
                                <div className="header-text">
                                    <h4 className="text-capitalize mb-0 fw-bold">{viewItem?.name}</h4>
                                    <p className="text-uppercase opacity-75 small mb-0">{viewItem?.company_name} | Salary Analytics</p>
                                </div>
                            </div>
                        </div>
                        <button className="glass-close" onClick={() => setViewItem(null)}> <i className='bx bx-x'></i> </button>
                    </div>

                    <div className="modal-body-premium p-4 bg-light">
                        <div className="mb-4">
                            <h6 className="fw-bold mb-3"><Briefcase size={16} className="me-2 text-primary" />Driver Salary Rates (Per Unit)</h6>
                            <div className="salary-rates-grid">
                                <div className="p-2 border rounded bg-white text-center shadow-sm">
                                    <small className="text-muted d-block uppercase fw-bold" style={{ fontSize: '9px' }}>Basic Salary</small>
                                    <strong className="text-primary">${Number(viewItem?.basic_salary || 0).toLocaleString()}</strong>
                                </div>
                                <div className="p-2 border rounded bg-white text-center shadow-sm">
                                    <small className="text-muted d-block uppercase fw-bold" style={{ fontSize: '9px' }}>Office OT</small>
                                    <strong className="text-warning">${Number(viewItem?.office_hours_ot || 0).toLocaleString()}</strong>
                                </div>
                                <div className="p-2 border rounded bg-white text-center shadow-sm">
                                    <small className="text-muted d-block uppercase fw-bold" style={{ fontSize: '9px' }}>After Office OT</small>
                                    <strong className="text-danger">${Number(viewItem?.after_office_ot || 0).toLocaleString()}</strong>
                                </div>
                                <div className="p-2 border rounded bg-white text-center shadow-sm">
                                    <small className="text-muted d-block uppercase fw-bold" style={{ fontSize: '9px' }}>Holiday OT</small>
                                    <strong className="text-info">${Number(viewItem?.holiday_ot || 0).toLocaleString()}</strong>
                                </div>
                            </div>
                        </div>

                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Card className="border-0 shadow-sm rounded-4 mb-4">
                                <CardBody className="p-3">
                                    <h6 className="fw-bold text-dark mb-3"><Calendar size={16} className="me-2 text-primary" /> Salary Period</h6>
                                    <Row className="g-3 align-items-end">
                                        <Col md={5}>
                                            <label className="extra-small fw-bold">From</label>
                                            <DatePicker value={salaryFilter.from_date} onChange={(val) => setSalaryFilter({ ...salaryFilter, from_date: val })} slotProps={pickerStyleProps} />
                                        </Col>
                                        <Col md={5}>
                                            <label className="extra-small fw-bold">To</label>
                                            <DatePicker value={salaryFilter.to_date} onChange={(val) => setSalaryFilter({ ...salaryFilter, to_date: val })} slotProps={pickerStyleProps} />
                                        </Col>
                                        <Col md={2}>
                                            <Button color="primary" className="w-100 h-45 rounded-3 fw-bold" onClick={() => fetchSalaryReport(viewItem.id)}>Calculate</Button>
                                        </Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </LocalizationProvider>

                        {salaryData && (
                            <div className="animate-fade-in">
                                <Row className="g-3 mb-4">
                                    <Col md={12}>
                                        <div className="salary-summary-card bg-white p-3 rounded-4 border shadow-sm text-center border-bottom border-success border-4">
                                            <p className="text-muted small mb-1 uppercase fw-bold">Estimated Payable Amount</p>
                                            <h2 className="text-success fw-bold mb-0">$ {salaryData.grand_total_salary?.toLocaleString()}</h2>
                                            <small className="text-muted">Total Active Hours: {salaryData.hours_breakdown?.total} Hrs</small>
                                        </div>
                                    </Col>
                                    <Col md={12}>
                                        <div className="salary-rates-grid">
                                            <SalaryBox label="Normal" hrs={salaryData.hours_breakdown?.normal} amt={salaryData.salary_breakdown?.normal} color="blue" />
                                            <SalaryBox label="Peak" hrs={salaryData.hours_breakdown?.peak} amt={salaryData.salary_breakdown?.peak} color="orange" />
                                            <SalaryBox label="OT" hrs={salaryData.hours_breakdown?.ot} amt={salaryData.salary_breakdown?.ot} color="red" />
                                            <SalaryBox label="Weekend" hrs={salaryData.hours_breakdown?.weekend} amt={salaryData.salary_breakdown?.weekend} color="green" />
                                            <SalaryBox label="Holiday" hrs={salaryData.hours_breakdown?.holiday} amt={salaryData.salary_breakdown?.holiday} color="purple" />
                                        </div>
                                    </Col>
                                </Row>

                                <Card className="border-0 shadow-sm rounded-4 overflow-hidden mt-4">
                                    <div className="p-3 bg-white border-bottom d-flex justify-content-between">
                                        <h6 className="mb-0 fw-bold">Recent Job Activity</h6>
                                        <Badge color="light" className="text-dark border">Detailed View</Badge>
                                    </div>
                                    <div className="table-responsive">
                                        <Table hover className="mb-0 align-middle text-center small-table bg-white">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th>Date</th>
                                                    <th>Normal</th>
                                                    <th>Peak</th>
                                                    <th>OT</th>
                                                    <th>Weekend</th>
                                                    <th>Holiday</th>
                                                    <th className="text-dark fw-bold">Earnings</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {salaryData?.jobs?.map((job, idx) => (
                                                    <tr key={idx}>
                                                        <td className="fw-bold">{dayjs(job.date).format("DD-MM-YYYY")}</td>
                                                        <td>{job.normal_hrs}h</td>
                                                        <td>{job.peak_hrs}h</td>
                                                        <td>{job.ot_hrs}h</td>
                                                        <td>{job.weekend_hrs}h</td>
                                                        <td>{job.holiday_hrs}h</td>
                                                        <td className="fw-bold text-success">${job.total_salary}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card>
                            </div>
                        )}
                        <div className="mt-4 d-flex gap-2">
                            <button className="btn btn-dark flex-grow-1 py-2 rounded-pill fw-bold shadow-sm" onClick={() => setViewItem(null)}>Close Analytics</button>
                        </div>
                    </div>
                </ModalBody >
            </Modal >

            <style>{`
                .premium-gradient { background: linear-gradient(135deg, #0e2e50 0%, #07c196 100%); }
                .h-45 { height: 45px; }
                .avatar-circle { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; flex-shrink: 0; }
                .avatar-large { width: 65px; height: 65px; font-size: 28px; border: 3px solid rgba(255,255,255,0.3); }
                .salary-rates-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
                .salary-item-card { background: white; padding: 12px 5px; border-radius: 12px; text-align: center; border-bottom: 4px solid transparent; transition: 0.3s; }
                .rate-blue { border-bottom-color: #0d6efd; }
                .rate-orange { border-bottom-color: #fd7e14; }
                .rate-red { border-bottom-color: #dc3545; }
                .rate-green { border-bottom-color: #198754; }
                .rate-purple { border-bottom-color: #6f42c1; }
                .small-table thead th { font-size: 11px; text-transform: uppercase; padding: 15px; color: #666; }
                .small-table tbody td { padding: 15px; border-bottom: 1px solid #f8f9fa; font-size: 13px; }
                .animate-fade-in { animation: fadeIn 0.4s ease-in; }
                .uppercase { text-transform: uppercase; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
                @media (max-width: 992px) { .salary-rates-grid { grid-template-columns: repeat(2, 1fr); } }
            `}</style>

            <DeleteModal
                isOpen={!!deleteTarget}
                toggle={() => setDeleteTarget(null)}
                title="Delete Driver"
                message={`Are you sure you want to delete driver ${deleteTarget?.name || ''}?`}
                onConfirm={async () => {
                    if (!deleteTarget) return;
                    setDeleting(true);
                    dispatch(loadingTrue());
                    const res = await Handler({
                        method: "delete",
                        url: "/driver/delete.php",
                        data: { id: deleteTarget.id }
                    });
                    dispatch(loadingFalse());
                    setDeleting(false);
                    if (res?.success) {
                        setDeleteTarget(null);
                        toast.success(res?.message || "Driver deleted successfully");
                        setData(prevData => prevData.filter(d => String(d.id) !== String(deleteTarget.id)));
                        loadDrivers();
                    } else {
                        setDeleteTarget(null);
                        toast.error(res?.message || "Failed to delete driver");
                    }
                }}
                loading={deleting}
            />
        </div>
    );
};

const SalaryBox = ({ label, hrs, amt, color }) => (
    <div className={`salary-item-card rate-${color} shadow-sm border`}>
        <span className="d-block small text-muted fw-bold mb-1" style={{ fontSize: '9px', textTransform: 'uppercase' }}>{label}</span>
        <strong className="d-block">${Number(amt || 0).toLocaleString()}</strong>
        <small className="hrs-text d-block text-primary fw-bold" style={{ fontSize: '10px' }}>{hrs || 0} Hrs</small>
    </div>
);

export default DriverList;